import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription, map, skip, switchMap } from 'rxjs';
import {
  CampaignReportDetailsTableComponent,
  ReportDetailsTableOptions,
  TArrangementSort,
} from '../../../../../../../components/tables/campaign-report-details-table/campaign-report-details-table.component';
import { GenderDistributionGraphComponent } from '../../../../../../../components/organization/campaigns/report-graphs/gender-distribution-graph/gender-distribution-graph.component';
import { AgeGroupDistributionGraphComponent } from '../../../../../../../components/organization/campaigns/report-graphs/age-group-distribution-graph/age-group-distribution-graph.component';
import { DeliveryRateByRecipientsGraphComponent } from '../../../../../../../components/organization/campaigns/report-graphs/delivery-rate-by-recipients-graph/delivery-rate-by-recipients-graph.component';
import { SmsTextComponent } from '../../../../../../../components/messages/sms-text/sms-text.component';
import { CampaignsService } from '../../../../../../../services/campaign/campaigns-api.service';
import {
  CampaignInsightsService,
  DeliveryFilter,
} from '../../../../../../../services/campaign/campaign-insights.service';
import {
  DeliverySummary,
  deliveredShare,
  formatShare,
} from '../../../../../../../services/message/delivery.service';
import {
  CampaignDetails,
  EngagementMessage,
  MessagesReport,
  MessagesReportDetails,
} from '../../../../types/campaignDetails';
import { MessageService } from '../../../../../../../services/message/message.service';
import { ReportChartData } from '../../../../types/messagesTypes';
import { formatCount, formatWhen, toDate } from '../../../../../../../../utils/sms';
import { ReceiptTable } from '../receipt-table';
import { orderedMessages, withMeta } from '../../../campaign-sends';

/**
 * One campaign message's report: how the chosen send went, who it reached
 * (searchable, filterable, paged, exportable) and the audience breakdown.
 */
@Component({
  selector: 'app-report-message-details',
  standalone: true,
  imports: [
    RouterLink,
    CampaignReportDetailsTableComponent,
    GenderDistributionGraphComponent,
    AgeGroupDistributionGraphComponent,
    DeliveryRateByRecipientsGraphComponent,
    SmsTextComponent,
  ],
  templateUrl: './report-message-details.component.html',
  styleUrl: './report-message-details.component.css',
})
export class ReportMessageDetailsComponent implements OnInit {
  route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private insights = inject(CampaignInsightsService);
  messageId!: string;
  campaignsService = inject(CampaignsService);
  messagesReport: MessagesReport | null = null;
  scheduleOptions: ReportDetailsTableOptions[] = [];
  message: EngagementMessage | null = null;

  // message report properties
  messageService = inject(MessageService);
  arrangementSort: TArrangementSort = '';
  loadingCharts: boolean = false;
  messageCharts: ReportChartData | null = null;

  table = new ReceiptTable(this.insights, this.destroyRef, () => this.messageId);

  /** The campaign's send list: loading, loaded, or unavailable. */
  sendsState: 'loading' | 'ready' | 'error' = 'loading';
  outcome: DeliverySummary | null = null;
  outcomeState: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  reportsLink: (string | number)[] = [];

  readonly formatCount = formatCount;

  private outcomeSub?: Subscription;

  get subject(): string {
    return (
      String(this.message?.messageSubject ?? '').trim() ||
      String(this.messagesReport?.message ?? '').trim() ||
      'Message report'
    );
  }

  get copy(): string {
    return String(this.message?.content || this.message?.messageTemplate || '');
  }

  get selectedSend(): MessagesReportDetails | null {
    return this.messagesReport?.reports?.find((send) => send.scheduleUuid === this.table.scheduleUuid) ?? null;
  }

  get selectedWhen(): string {
    const at = toDate(this.selectedSend?.messageSchedules ?? null);
    return at ? formatWhen(at) : '';
  }

  get share(): string {
    return this.outcome ? formatShare(deliveredShare(this.outcome)) : '';
  }

  get outcomeBreakdown(): string {
    if (!this.outcome) return '';
    const { total, delivered, failed, inFlight } = this.outcome;
    const parts = [`${formatCount(delivered)} delivered`];
    if (failed) parts.push(`${formatCount(failed)} failed`);
    if (inFlight) parts.push(`${formatCount(inFlight)} with no receipt yet`);
    return `${parts.join(', ')}, of ${formatCount(total)} recipients`;
  }

  // ── table events ──

  updateSearchTerm(term: string) {
    this.table.setSearch(term);
  }

  updateSortArrangementCriteria(criteria: TArrangementSort) {
    this.arrangementSort = criteria;
    this.table.setSort(criteria);
  }

  updateMessageSchedule(uuid: string) {
    this.table.setSchedule(uuid);
    this.loadOutcome();
  }

  retryOutcome() {
    this.loadOutcome();
  }

  updateStatusFilter(filter: DeliveryFilter) {
    this.table.setFilter(filter);
  }

  showFailed() {
    this.table.setFilter('failed');
  }

  downloadCsv() {
    this.table.download('campaign message', this.subject, this.selectedWhen);
  }

  ngOnInit(): void {
    this.messageId = this.route.snapshot.paramMap.get('messageId')!;
    const campaignUuid = this.campaignsService.campaignUuid;
    const idRoute = this.route.snapshot.pathFromRoot.find((r) => r.paramMap.has('id'));
    const campaignId = idRoute?.paramMap.get('id') ?? '';
    this.reportsLink = ['/organization/campaigns', campaignId, campaignUuid, 'reports'];

    // The message itself (subject, copy, sender) from the campaign already
    // loaded by the page. The route carries the message uuid, which only the
    // message's own GET has, so match on that.
    this.campaignsService.campaignDetails
      .pipe(
        switchMap((data) => {
          const messages = orderedMessages(data as CampaignDetails);
          return this.insights
            .messageMeta(messages.map((m) => m.id))
            .pipe(map((meta) => messages.map((m) => withMeta(m, meta[m.id]))));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((messages) => {
        this.message = messages.find((m) => !!m.uuid && m.uuid === this.messageId) ?? null;
      });

    // The message's sends, to choose which one the table shows.
    if (campaignUuid) {
      this.insights
        .messageReports(campaignUuid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (reports) => this.applySends(reports),
          error: () => {
            this.sendsState = 'error';
            // Without the send list, show every recipient of the message.
            this.table.load();
          },
        });
    } else {
      this.sendsState = 'error';
      this.table.load();
    }

    // Charts: group, gender and age for this message.
    this.messageService.fetchMessageChartReport(this.messageId);
    this.messageService.getMessageReportChart
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((report) => {
        this.messageCharts = report;
      });
    this.messageService.getLoadingMessageReportChart
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.loadingCharts = value;
      });
  }

  private applySends(reports: MessagesReport[]) {
    this.messagesReport = reports.find((r) => r?.referenceId == this.messageId) ?? null;
    const sends = [...(this.messagesReport?.reports ?? [])]
      .filter((send) => !!send?.scheduleUuid)
      .sort(
        (a, b) =>
          (toDate(a.messageSchedules ?? null)?.getTime() ?? 0) -
          (toDate(b.messageSchedules ?? null)?.getTime() ?? 0)
      );

    //getting schedule options for table component
    this.scheduleOptions = sends.map((send) => {
      const at = toDate(send.messageSchedules ?? null);
      const status = String(send.scheduleStatus ?? '').replace(/_/g, ' ').toLowerCase();
      return {
        name: `${at ? formatWhen(at) : 'No send time'}${status ? ` · ${status}` : ''}`,
        id: send.scheduleUuid,
      };
    });
    this.sendsState = 'ready';

    // Open on the latest send that has gone out, else the first one.
    const now = Date.now();
    const sent = sends.filter((send) => (toDate(send.messageSchedules ?? null)?.getTime() ?? 0) <= now);
    const initial = (sent[sent.length - 1] ?? sends[0])?.scheduleUuid ?? '';
    this.table.scheduleUuid = initial;
    this.table.load();
    this.loadOutcome();
  }

  private loadOutcome() {
    this.outcomeSub?.unsubscribe();
    const uuid = this.table.scheduleUuid;
    if (!uuid) {
      this.outcome = null;
      this.outcomeState = 'idle';
      return;
    }
    this.outcomeState = 'loading';
    this.outcomeSub = this.insights
      .sendOutcome(uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => {
          this.outcome = summary;
          this.outcomeState = 'ready';
        },
        error: () => {
          this.outcomeState = 'error';
        },
      });
  }
}
